import React, { createContext, useState, useContext, useEffect } from 'react';
import { contactService } from '../services/contactService';

const EnquiryContext = createContext();

export const useEnquiry = () => {
  const context = useContext(EnquiryContext);
  if (!context) {
    throw new Error('useEnquiry must be used within EnquiryProvider');
  }
  return context;
};

export const EnquiryProvider = ({ children }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch enquiries from database
  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      console.log('=== EnquiryContext: Fetching from DATABASE ===');
      
      const data = await contactService.getContacts();
      console.log('Contacts from database:', data);
      
      // Handle pagination response
      const enquiriesArray = data.data || data;
      setEnquiries(enquiriesArray);
      
      console.log(`✅ Loaded ${enquiriesArray.length} enquiries from database`);
    } catch (err) {
      console.error('❌ Error loading enquiries from database:', err);
      setError('Failed to load enquiries');
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchEnquiries();
  }, []);

  // Add enquiry (public - from contact form)
  const addEnquiry = async (enquiryData) => {
    try {
      console.log('Submitting enquiry to database...');
      const result = await contactService.submitContact(enquiryData);
      
      if (result.success) {
        console.log('✅ Enquiry submitted successfully');
        return result.data;
      }
    } catch (err) {
      console.error('❌ Error submitting enquiry:', err);
      throw new Error('Failed to submit enquiry');
    }
  };

  // Mark as read (admin)
  const markAsRead = async (id) => {
    try {
      await contactService.updateStatus(id, 'read');
      await fetchEnquiries(); // Refresh
    } catch (err) {
      console.error('❌ Error marking as read:', err);
    }
  };

  // Mark as replied (admin)
  const markAsReplied = async (id) => {
    try {
      await contactService.updateStatus(id, 'replied');
      await fetchEnquiries(); // Refresh
    } catch (err) {
      console.error('❌ Error marking as replied:', err);
    }
  };

  // Delete enquiry (admin)
  const deleteEnquiry = async (id) => {
    try {
      await contactService.deleteContact(id);
      setEnquiries(enquiries.filter(e => e.id !== id));
    } catch (err) {
      console.error('❌ Error deleting enquiry:', err);
    }
  };

  // Get unread count
  const getUnreadCount = () => {
    return enquiries.filter(e => e.status === 'new').length;
  };

  const value = {
    enquiries,
    loading,
    error,
    addEnquiry,
    markAsRead,
    markAsReplied,
    deleteEnquiry,
    getUnreadCount,
    refreshEnquiries: fetchEnquiries
  };

  return <EnquiryContext.Provider value={value}>{children}</EnquiryContext.Provider>;
};
